import { lerDecimal } from '@equiview/domain';
import { useState } from 'react';
import { formatarValor, traitPorCodigo, unidadeCurta } from '../dados/catalogo.js';
import { useConsulta, useFonte } from '../dados/contexto.js';
import type { Emenda, FichaDeColeta, Manejo, Medida, Usuario } from '../dados/fonte.js';
import { Botao, Campo, classeCampo, Etiqueta, Painel, Selecao } from './ui/index.js';

const LADO = { E: ', esquerdo', D: ', direito', NA: '' } as const;
const CAMPOS_DE_MANEJO: Partial<Record<keyof Manejo, { rotulo: string; casas: number }>> = {
  weight_kg: { rotulo: 'Peso (kg)', casas: 0 },
  bcs_henneke: { rotulo: 'ECC (1–9)', casas: 0 },
  gestation_months: { rotulo: 'Meses de gestação', casas: 1 },
  training_months: { rotulo: 'Meses de treino', casas: 1 },
};
const ROTULO_STATUS: Record<string, string> = {
  rascunho: 'rascunho',
  fechada: 'fechada',
  validada: 'validada',
  reaberta: 'reaberta',
};

const rotuloMedida = (m: Medida, segunda: boolean) =>
  `${traitPorCodigo.get(m.trait_code)?.label ?? m.trait_code}${LADO[m.side]}${segunda ? ' (2ª medição)' : ''}`;

/**
 * Ficha fechada: correcao so por emenda, com o valor antigo, o novo e o motivo.
 * O servidor confere que o valor antigo ainda e o atual e aplica; o historico
 * fica aqui embaixo. Reabrir e validar sao do admin.
 */
export function PainelDeEmendas({ ficha, usuario }: { ficha: FichaDeColeta; usuario: Usuario | null }) {
  const cadastro = useFonte().cadastro!;
  const { avaliacao } = ficha;
  const admin = usuario?.papel === 'admin';
  const historico = useConsulta((f) => f.cadastro!.emendas(avaliacao.id), [avaliacao.id]);

  const medidas = [
    ...ficha.medidas.filter((m) => m.id).map((m) => ({ m, segunda: false })),
    ...ficha.medidas2.filter((m) => m.id).map((m) => ({ m, segunda: true })),
  ];
  const alvos = [
    ...medidas.map(({ m, segunda }) => ({ chave: `m:${m.id}`, rotulo: rotuloMedida(m, segunda) })),
    ...Object.entries(CAMPOS_DE_MANEJO).map(([campo, c]) => ({ chave: `manejo:${campo}`, rotulo: c!.rotulo })),
  ];

  const [alvo, setAlvo] = useState('');
  const [novo, setNovo] = useState('');
  const [naoSeAplica, setNaoSeAplica] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [motivoStatus, setMotivoStatus] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  const medidaAlvo = alvo.startsWith('m:') ? medidas.find(({ m }) => `m:${m.id}` === alvo)?.m : undefined;
  const campoAlvo = alvo.startsWith('manejo:') ? (alvo.slice(7) as keyof Manejo) : undefined;
  const trait = medidaAlvo ? traitPorCodigo.get(medidaAlvo.trait_code) : undefined;
  const casas = trait?.decimals ?? (campoAlvo ? (CAMPOS_DE_MANEJO[campoAlvo]?.casas ?? 1) : 1);
  const atual = medidaAlvo
    ? medidaAlvo.not_applicable
      ? 'não se aplica'
      : `${formatarValor(medidaAlvo.value_num, trait)}${unidadeCurta(trait)}`
    : campoAlvo
      ? String(ficha.manejo[campoAlvo] ?? '—')
      : null;
  const lido = lerDecimal(novo, casas);

  const agir = async (acao: () => Promise<void>, mensagem: string) => {
    setErro(null);
    setOk(null);
    setGravando(true);
    try {
      await acao();
      setOk(mensagem);
      setAlvo('');
      setNovo('');
      setMotivo('');
      setMotivoStatus('');
      setNaoSeAplica(false);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setGravando(false);
    }
  };

  const registrar = () =>
    agir(async () => {
      if (medidaAlvo?.id) await cadastro.emendarMedida(medidaAlvo.id, { valor: naoSeAplica ? null : lido, naoSeAplica }, motivo);
      else if (campoAlvo) await cadastro.emendarManejo(avaliacao.id, campoAlvo, lido as Manejo[keyof Manejo], motivo);
    }, 'Emenda registrada. Ela sobe na próxima sincronização, e o servidor confere o valor antigo.');

  const podeRegistrar =
    !!alvo && motivo.trim().length >= 10 && (naoSeAplica || (lido != null && !Number.isNaN(lido)));

  return (
    <Painel
      titulo="Emendas"
      acoes={<Etiqueta tom={avaliacao.status === 'validada' ? 'ok' : 'neutro'}>Ficha {ROTULO_STATUS[avaliacao.status]}</Etiqueta>}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <p className="text-sm text-apagado">
            Ficha fechada não é editada: cada correção é uma emenda, com o valor antigo, o novo e o motivo. Nada é apagado.
          </p>
          <Selecao rotulo="O que corrigir" value={alvo} onChange={(e) => setAlvo(e.target.value)}>
            <option value="">Escolha a medida ou o campo</option>
            {alvos.map((a) => (
              <option key={a.chave} value={a.chave}>
                {a.rotulo}
              </option>
            ))}
          </Selecao>
          {alvo && (
            <>
              <p className="text-sm">
                Valor atual: <span className="numero font-medium">{atual}</span>
              </p>
              <div className="flex flex-wrap items-end gap-4">
                <Campo rotulo="Valor correto">
                  {(a) => (
                    <input
                      {...a}
                      type="text"
                      inputMode="decimal"
                      disabled={naoSeAplica}
                      value={novo}
                      onChange={(e) => setNovo(e.target.value)}
                      className={`${classeCampo(!!novo && Number.isNaN(lido))} numero max-w-36 text-right`}
                    />
                  )}
                </Campo>
                {medidaAlvo && (
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <input type="checkbox" checked={naoSeAplica} onChange={(e) => setNaoSeAplica(e.target.checked)} />
                    Não se aplica
                  </label>
                )}
              </div>
              <Campo rotulo="Motivo" obrigatorio ajuda="Pelo menos 10 caracteres. Fica registrado para sempre.">
                {(a) => (
                  <textarea {...a} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} className={classeCampo()} />
                )}
              </Campo>
              <Botao variante="principal" disabled={!podeRegistrar} carregando={gravando} onClick={() => void registrar()}>
                Registrar emenda
              </Botao>
            </>
          )}
        </div>

        {admin && (
          <div className="space-y-3 border-t border-borda pt-4">
            <p className="text-sm font-medium">Coordenação</p>
            <Campo rotulo="Motivo ou observação" ajuda="Pelo menos 10 caracteres.">
              {(a) => (
                <textarea {...a} rows={2} value={motivoStatus} onChange={(e) => setMotivoStatus(e.target.value)} className={classeCampo()} />
              )}
            </Campo>
            <div className="flex flex-wrap gap-2">
              {avaliacao.status === 'fechada' && (
                <Botao
                  variante="principal"
                  disabled={motivoStatus.trim().length < 10 || gravando}
                  onClick={() => void agir(() => cadastro.validarFicha(avaliacao.id, motivoStatus), 'Ficha validada.')}
                >
                  Validar ficha
                </Botao>
              )}
              <Botao
                disabled={motivoStatus.trim().length < 10 || gravando}
                onClick={() => void agir(() => cadastro.reabrirFicha(avaliacao.id, motivoStatus), 'Ficha reaberta para correção.')}
              >
                Reabrir ficha
              </Botao>
            </div>
          </div>
        )}

        {erro && <p className="text-sm text-erro">{erro}</p>}
        {ok && <p className="text-sm text-ok">{ok}</p>}

        {historico.estado === 'pronto' && historico.dado.length > 0 && (
          <div className="border-t border-borda pt-4">
            <p className="mb-2 text-sm font-medium">Histórico</p>
            <ul className="space-y-2 text-sm">
              {historico.dado.map((e) => (
                <li key={e.id} className="border-l-2 border-borda-forte pl-3">
                  <p>
                    <span className="font-medium">{descreverEmenda(e, ficha)}</span>: {e.old_value ?? 'vazio'} → {e.new_value ?? 'vazio'}
                  </p>
                  <p className="text-xs text-apagado">
                    {e.autor} · {e.at ? new Date(e.at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'} ·{' '}
                    {e.reason}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Painel>
  );
}

function descreverEmenda(e: Emenda, ficha: FichaDeColeta): string {
  if (e.entity === 'assessment') return 'Situação da ficha';
  if (e.entity === 'animal_management') return CAMPOS_DE_MANEJO[e.field as keyof Manejo]?.rotulo ?? e.field;
  const m = [...ficha.medidas, ...ficha.medidas2].find((x) => x.id === e.entity_id);
  const campo = e.field === 'not_applicable' ? ' (não se aplica)' : '';
  return m ? `${traitPorCodigo.get(m.trait_code)?.label ?? m.trait_code}${LADO[m.side]}${campo}` : 'Medida';
}
